import axios from "@/Data/Axios";
import type {
    Event as BaseEvent,
    EventRegistration,
    EventType,
    EventStatus,
    EventFormat
} from 'ai-api/types/admission';

// Extended Event type with image_url for banner display
export interface Event extends BaseEvent {
    image_url?: string;
    banner_url?: string;
}

// API Request/Response interfaces
export interface EventListParams {
    eventid?: string;
    universityid?: string;
    trackid?: string;
    type?: EventType;
    status?: EventStatus;
    from_at?: number;
    to_at?: number;
}

export interface CreateEventRequest {
    eventid: string;
    universityid: string;
    trackid?: string;
    name?: string;
    description?: string;
    type?: EventType;
    format?: EventFormat;
    location?: string;
    online_link?: string;
    start_at?: number;
    end_at?: number;
    capacity?: number;
    registration_required?: boolean;
    registration_deadline?: number;
}

export interface UpdateEventRequest {
    eventid: string;
    name?: string;
    description?: string;
    type?: EventType;
    format?: EventFormat;
    status?: EventStatus;
    location?: string;
    online_link?: string;
    start_at?: number;
    end_at?: number;
    capacity?: number;
    registration_required?: boolean;
    registration_deadline?: number;
}

export interface EventRegistrationListParams {
    eventid?: string;
    userid?: string;
}

// API Service class for Event management
export class EventService {
    private static readonly BASE_URL = '/admission/event';
    private static readonly REGISTRATION_URL = '/admission/event/registration';

    // List events
    static async listEvents(params?: EventListParams): Promise<Event[]> {
        const response = await axios.get<{ code: number; data: Event[] }>(
            this.BASE_URL,
            { params }
        );
        return response.data.data;
    }

    // Get single event
    static async getEvent(eventid: string): Promise<Event | null> {
        const events = await this.listEvents({ eventid });
        return events[0] || null;
    }

    // Create event (manager only)
    static async createEvent(request: CreateEventRequest): Promise<Event> {
        const response = await axios.post<{ code: number; data: Event }>(
            this.BASE_URL,
            request
        );
        return response.data.data;
    }

    // Update event (manager only)
    static async updateEvent(request: UpdateEventRequest): Promise<Event> {
        const response = await axios.put<{ code: number; data: Event }>(
            this.BASE_URL,
            request
        );
        return response.data.data;
    }

    // Delete event (manager only)
    static async deleteEvent(eventid: string): Promise<void> {
        await axios.delete(this.BASE_URL, { params: { eventid } });
    }

    // Register for event
    static async registerForEvent(eventid: string): Promise<EventRegistration> {
        const response = await axios.post<{ code: number; data: EventRegistration }>(
            this.REGISTRATION_URL,
            { eventid }
        );
        return response.data.data;
    }

    // Unregister from event
    static async unregisterFromEvent(eventid: string): Promise<void> {
        await axios.delete(this.REGISTRATION_URL, { params: { eventid } });
    }

    // List event registrations
    static async listRegistrations(params?: EventRegistrationListParams): Promise<EventRegistration[]> {
        const response = await axios.get<{ code: number; data: EventRegistration[] }>(
            this.REGISTRATION_URL,
            { params }
        );
        return response.data.data;
    }
}

// Helper functions
export const isEventUpcoming = (event: Event): boolean => {
    return event.start_at > Date.now() / 1000;
};

export const isEventPast = (event: Event): boolean => {
    return event.end_at < Date.now() / 1000;
};

export const isEventOngoing = (event: Event): boolean => {
    const now = Date.now() / 1000;
    return event.start_at <= now && event.end_at >= now;
};

export const canRegisterForEvent = (event: Event): boolean => {
    if (!event.registration_required) return false;
    if (event.status !== 'active') return false;
    if (event.registered) return false;

    const now = Date.now() / 1000;
    if (event.registration_deadline && event.registration_deadline < now) return false;

    return true;
};

export const formatEventDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const getEventTypeLabel = (type: EventType): string => {
    const labels: Record<EventType, string> = {
        info_session: 'Information Session',
        entrance_exam: 'Entrance Exam',
        interview: 'Interview',
        consultation: 'Consultation',
        deadline: 'Deadline',
        announcement: 'Announcement',
        other: 'Other'
    };
    return labels[type] || type;
};

export const getEventStatusBadgeColor = (status: EventStatus): string => {
    const colors: Record<EventStatus, string> = {
        draft: 'gray',
        active: 'green',
        cancelled: 'red'
    };
    return colors[status] || 'gray';
};